import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Radio,
  RadioGroup,
  Tooltip,
  FormControlLabel,
  FormControl,
  Checkbox,
  Select,
  MenuItem,
  InputLabel,
  CircularProgress,
} from "@mui/material";
import {
  ArrowBack,
  ArrowForward,
  Cancel,
  CheckCircle,
  Videocam,
  Close,
  Description,
  ExpandMore,
  Fullscreen,
  OpenInNew,
  MenuBook,
  OndemandVideo,
  PlayCircle,
  RadioButtonUnchecked,
  Reply,
  LockOutlined,
  ScreenRotation,
  Send,
  ThumbUp,
  ThumbUpOffAlt,
  TextSnippet,
  Timer,
  DeleteOutline,
  Quiz,
  EmojiEvents,
  SentimentDissatisfied,
} from "@mui/icons-material";
import {
  MEMBERSHIP_COLORS,
  ContentContainer,
} from "../../components/membership/MembershipStyles";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { localizedTitle, pickLocalized } from "../../utils/localizedContent";
import coursesService, {
  CourseComment,
  CourseStats,
  PublicCourseDetail,
  PublicCourseItem,
  PublicCourseItemCompanionVideo,
  SurveyQuestion,
} from "../../services/coursesService";
import { PDFViewer } from "../../components/admin/PDFViewer";
import { VideoPlayer } from "../../components/common/VideoPlayer";
import { VideoPlayback, isPlayable, resolveVideoPlayback } from "../../utils/videoPlayback";
import {
  VIDEO_ADDON_PER_COURSE, VIDEO_ADDON_PLAN_ID, VIDEO_ADDON_PLAN_NAME, computePlanPrice,
} from "../../utils/planPricing";

const formatCommentDate = (isoDate: string, locale: string) => {
  const dt = new Date(isoDate);
  if (Number.isNaN(dt.getTime())) return "";
  // "ar" gives Arabic month names and the locale's own time convention.
  return dt.toLocaleString(locale === "ar" ? "ar" : undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const sanitizeLetterHtml = (rawHtml: string) => {
  if (!rawHtml) return "";

  // Remove obvious script/style payloads and inline handlers.
  const withoutScripts = rawHtml
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, "")
    .replace(/\son\w+\s*=\s*"[^"]*"/gi, "")
    .replace(/\son\w+\s*=\s*'[^']*'/gi, "")
    .replace(/\son\w+\s*=\s*[^\s>]+/gi, "");

  // If editor content is plain text, preserve line breaks.
  const hasHtmlTags = /<[^>]+>/.test(withoutScripts);
  if (!hasHtmlTags) {
    return withoutScripts
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\n/g, "<br />");
  }

  return withoutScripts;
};

type TrackedCourseItem = {
  phaseId: string;
  phaseTitle: string;
  item: PublicCourseItem;
};

type ContentPreview =
  | {
      type: "pdf";
      title: string;
      pdfUrl: string;
      pdfUrlAr?: string;
      pdfPages?: number;
      /** Paid add-on that goes with this leaflet, when one is attached. */
      companion?: PublicCourseItemCompanionVideo | null;
    }
  | {
      type: "letter";
      title: string;
      letterContent: string;
    };

type PdfViewLang = "en" | "ar";

// Streams PDF through backend -> blob URL so no direct R2 calls in the browser
const MemberPdfViewer: React.FC<{ pdfKey: string; title: string }> = ({ pdfKey, title }) => {
  const { t } = useLanguage();
  const [blobUrl, setBlobUrl] = React.useState("");
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!pdfKey) { setLoading(false); return; }
    let objectUrl = "";
    setLoading(true);
    setBlobUrl("");

    coursesService.streamPdf(pdfKey)
      .then(data => {
        const blob = new Blob([data], { type: "application/pdf" });
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      })
      .catch(() => setBlobUrl(""))
      .finally(() => setLoading(false));

    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [pdfKey]);

  if (loading) return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
      <CircularProgress size={32} />
    </Box>
  );

  if (!blobUrl) return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
      <Typography sx={{ color: "#EF4444" }}>{t.courseDetail.viewer.pdfLoadFailed}</Typography>
    </Box>
  );

  return <PDFViewer url={blobUrl} title={title} />;
};

const CourseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { user } = useAuth();
  const { t, language, isRTL } = useLanguage();
  const tcd = t.courseDetail;
  const listSeparator = isRTL ? "، " : ", ";
  const dir = isRTL ? "rtl" : "ltr";

  // Course/phase/lesson text comes from the database in both languages —
  // resolve against the active locale, falling back to whichever exists.
  const localized = useCallback(
    (en?: string | null, ar?: string | null) => pickLocalized(en, ar, language),
    [language]
  );

  // Durations are locale-formatted the same way everywhere on this page.
  const formatDuration = useCallback(
    (seconds: number) => {
      if (!seconds || seconds <= 0) return tcd.duration.quickStart;
      const totalMins = Math.round(seconds / 60);
      if (totalMins < 60) return tcd.duration.minutes(totalMins);
      return tcd.duration.hoursMinutes(Math.floor(totalMins / 60), totalMins % 60);
    },
    [tcd]
  );

  const [course, setCourse] = useState<PublicCourseDetail | null>(null);
  const [stats, setStats] = useState<CourseStats | null>(null);
  const [loading, setLoading] = useState(true);
  // Load failures are stored as a reason + optional server message; the
  // user-facing wording is resolved at render time so it follows the
  // active language without refetching.
  const [error, setError] = useState<{ reason: "locked" | "load"; message?: string } | null>(null);
  const [lessonPreview, setLessonPreview] = useState<{
    title: string;
    playback: VideoPlayback;
    poster?: string;
  } | null>(null);
  const [contentPreview, setContentPreview] = useState<ContentPreview | null>(null);
  const [pdfViewLang, setPdfViewLang] = useState<"en" | "ar">("en");
  const [completedItemIds, setCompletedItemIds] = useState<string[]>([]);
  const [isLocked, setIsLocked] = useState(false);
  const [lockReason, setLockReason] = useState("");
  // The member owns this course but not its companion video — the server says
  // the video can be bought on its own.
  const [canBuyVideo, setCanBuyVideo] = useState(false);
  const [savingProgress, setSavingProgress] = useState(false);
  const [comments, setComments] = useState<CourseComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentInput, setCommentInput] = useState("");
  const [postingComment, setPostingComment] = useState(false);
  const [replyOpenFor, setReplyOpenFor] = useState<string | null>(null);
  const [replyDrafts, setReplyDrafts] = useState<Record<string, string>>({});
  const videoFrameRef = useRef<HTMLDivElement | null>(null);

  // Survey / Quiz state
  const [surveyOpen, setSurveyOpen] = useState(false);
  const [surveyItem, setSurveyItem] = useState<PublicCourseItem | null>(null);
  const [surveyAnswers, setSurveyAnswers] = useState<Record<string, string | string[]>>({});
  const [surveySubmitting, setSurveySubmitting] = useState(false);
  const [surveyError, setSurveyError] = useState<string | null>(null);
  const [surveyResult, setSurveyResult] = useState<{
    mode: string;
    score?: number;
    passed?: boolean;
    correctAnswers?: number;
    totalQuestions?: number;
    message?: string;
    questionResults?: {
      questionId: string;
      isCorrect: boolean | null;
      userAnswer: string | string[];
      correctAnswer?: string | string[];
    }[];
  } | null>(null);

  const updateCommentTree = (
    list: CourseComment[],
    commentId: string,
    updater: (comment: CourseComment) => CourseComment
  ): CourseComment[] => {
    return list.map((comment) => {
      if (comment._id === commentId) {
        return updater(comment);
      }
      if (comment.replies?.length) {
        return {
          ...comment,
          replies: comment.replies.map((reply) =>
            reply._id === commentId ? updater(reply) : reply
          ),
        };
      }
      return comment;
    });
  };

  const removeCommentFromTree = (list: CourseComment[], commentId: string): CourseComment[] => {
    return list
      .filter((comment) => comment._id !== commentId)
      .map((comment) => ({
        ...comment,
        replies: (comment.replies || []).filter((reply) => reply._id !== commentId),
      }));
  };

  // Companion videos reuse the same player dialog as a normal video lesson. A
  // locked add-on resolves to `none`, so there is nothing to open.
  const openCompanionVideo = (
    companion: PublicCourseItemCompanionVideo | null | undefined,
    fallbackTitle: string
  ) => {
    const playback = resolveVideoPlayback(companion);
    if (playback.kind === "none") return;
    setLessonPreview({
      title: companion?.title || fallbackTitle || tcd.viewer.lessonFallbackTitle,
      playback,
      poster: companion?.thumbnailUrl || undefined,
    });
  };

  // Sends the member to checkout for just this course's companion video. The
  // server re-checks that they own the leaflet before accepting the order.
  const buyCompanionVideo = () => {
    if (!course) return;
    const courseId = String(course._id);
    navigate("/payment", {
      state: {
        planId: VIDEO_ADDON_PLAN_ID,
        planName: VIDEO_ADDON_PLAN_NAME,
        price: computePlanPrice({ planId: VIDEO_ADDON_PLAN_ID, videoCourseIds: [courseId] }),
        billing: "one-time",
        selectedCourseIds: [],
        selectedCourseTitles: [],
        videoCourseIds: [courseId],
        videoCourseTitles: [localizedTitle(course, language)],
      },
    });
  };

  const hasReadableContent = (item: PublicCourseItem) => {
    if (item.type === "video") return isPlayable(item.video);
    if (item.type === "pdf") return !!(item.pdfUrl || item.pdfUrlAr);
    if (item.type === "letter") return !!(item.letterContent?.trim() || item.letterContentAr?.trim());
    if (item.type === "survey") return !!(item.surveyConfig?.questions?.length);
    return false;
  };

  const openCourseItem = (item: PublicCourseItem) => {
    if (item.type === "video") {
      const playback = resolveVideoPlayback(item.video);
      if (playback.kind === "none") return;
      setLessonPreview({
        title:
          localized(item.title, item.titleAr) ||
          item.video?.title ||
          tcd.viewer.lessonFallbackTitle,
        playback,
        poster: item.video?.thumbnailUrl || undefined,
      });
      return;
    }

    if (item.type === "pdf" && (item.pdfUrl || item.pdfUrlAr)) {
      // default to current app language; fall back to whichever version exists
      const defaultLang: PdfViewLang =
        language === "ar" && item.pdfUrlAr ? "ar"
        : item.pdfUrl ? "en"
        : "ar";
      setPdfViewLang(defaultLang);
      setContentPreview({
        type: "pdf",
        title: localized(item.title, item.titleAr) || tcd.viewer.pdfFallbackTitle,
        pdfUrl: item.pdfUrl || "",
        pdfUrlAr: item.pdfUrlAr || "",
        pdfPages: item.pdfPages,
        companion: item.companionVideo || null,
      });
      return;
    }

    if (item.type === "letter" && (item.letterContent?.trim() || item.letterContentAr?.trim())) {
      setContentPreview({
        type: "letter",
        title: localized(item.title, item.titleAr) || tcd.viewer.letterFallbackTitle,
        letterContent: localized(item.letterContent, item.letterContentAr),
      });
      return;
    }

    if (item.type === "survey" && item.surveyConfig?.questions?.length) {
      setSurveyItem(item);
      setSurveyAnswers({});
      setSurveyResult(null);
      setSurveyError(null);
      setSurveyOpen(true);
    }
  };

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      try {
        setLoading(true);
        setError(null);
        const result = await coursesService.getCourse(id);
        setCourse(result.course);
        setStats(result.stats);
        setCompletedItemIds(result.progress?.completedItemIds || []);
        setIsLocked(!!result.access?.isLocked);
        setLockReason(result.access?.lockReason || "");
        setCanBuyVideo(!!result.access?.canBuyCompanionVideo);
      } catch (err: any) {
        if (err?.response?.data?.code === "COURSE_LOCKED") {
          setError({ reason: "locked" });
          setLockReason(err?.response?.data?.lockReason || "");
          return;
        }
        setError({ reason: "load", message: err?.response?.data?.message });
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id]);

  const loadComments = useCallback(async () => {
    if (!id) return;
    try {
      setCommentsLoading(true);
      const result = await coursesService.getCourseComments(id, { page: 1, limit: 50 });
      setComments(result.comments || []);
    } catch {
      setComments([]);
    } finally {
      setCommentsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  const trackedItems = useMemo<TrackedCourseItem[]>(() => {
    if (!course) return [];

    return course.phases
      .slice()
      .sort((a, b) => a.order - b.order)
      .flatMap((phase) =>
        phase.items
          .slice()
          .sort((a, b) => a.order - b.order)
          .map((item) => ({
            phaseId: phase._id,
            phaseTitle: localized(phase.title, phase.titleAr),
            item,
          }))
      );
  }, [course, localized]);

  const completedCount = useMemo(() => {
    return trackedItems.filter((entry) => completedItemIds.includes(entry.item._id)).length;
  }, [trackedItems, completedItemIds]);

  const progressPct = trackedItems.length
    ? Math.round((completedCount / trackedItems.length) * 100)
    : 0;

  const nextLearningItem = useMemo(() => {
    return trackedItems.find((entry) => {
      const incomplete = !completedItemIds.includes(entry.item._id);
      const readable =
        (entry.item.type === "video" && isPlayable(entry.item.video)) ||
        (entry.item.type === "pdf" && !!(entry.item.pdfUrl || entry.item.pdfUrlAr)) ||
        (entry.item.type === "letter" &&
          !!(entry.item.letterContent?.trim() || entry.item.letterContentAr?.trim())) ||
        (entry.item.type === "survey" && !!(entry.item.surveyConfig?.questions?.length));
      return incomplete && readable;
    });
  }, [trackedItems, completedItemIds]);

  const continueLabel = useMemo(() => {
    const cta = tcd.continueCta;
    if (!nextLearningItem) return cta.allDone;
    if (nextLearningItem.item.type === "video") return cta.video;
    if (nextLearningItem.item.type === "pdf") return cta.pdf;
    if (nextLearningItem.item.type === "survey") {
      const mode = nextLearningItem.item.surveyConfig?.mode;
      return mode === "quiz" ? cta.quiz : cta.survey;
    }
    return cta.letter;
  }, [nextLearningItem, tcd]);

  const toggleItemComplete = async (itemId: string) => {
    if (!id || isLocked || savingProgress) return;

    const nextIds = completedItemIds.includes(itemId)
      ? completedItemIds.filter((existingId) => existingId !== itemId)
      : [...completedItemIds, itemId];

    setCompletedItemIds(nextIds);
    setSavingProgress(true);

    try {
      const result = await coursesService.updateCourseProgress(id, nextIds);
      setCompletedItemIds(result.completedItemIds || []);
      setIsLocked(!!result.isLocked);
      setLockReason(result.lockReason || "");
    } catch (err: any) {
      // Revert optimistic state on failure
      setCompletedItemIds(completedItemIds);
      if (err?.response?.data?.code === "COURSE_LOCKED") {
        setIsLocked(true);
        setLockReason(err?.response?.data?.lockReason || "");
      }
    } finally {
      setSavingProgress(false);
    }
  };

  const submitSurvey = async () => {
    if (!id || !surveyItem?.surveyConfig) return;
    const config = surveyItem.surveyConfig;

    // Validate required questions
    for (const q of config.questions) {
      if (!q.required) continue;
      const ans = surveyAnswers[q.id];
      const isEmpty = !ans || (Array.isArray(ans) ? ans.length === 0 : ans.trim() === "");
      if (isEmpty) {
        setSurveyError(`Please answer: "${q.text || `Question ${config.questions.indexOf(q) + 1}`}"`);
        return;
      }
    }

    setSurveyError(null);
    setSurveySubmitting(true);
    try {
      // Build a keyed object { [questionId]: answer } for O(1) lookup on the backend
      const answers: Record<string, string | string[]> = {};
      for (const q of config.questions) {
        answers[q.id] = surveyAnswers[q.id] ?? (q.type === "checkbox" ? [] : "");
      }
      const result = await coursesService.submitSurvey(id, surveyItem._id, answers);

      // Compute per-question correctness client-side (surveyConfig has correctAnswer per question)
      const questionResults = config.questions.map((q) => {
        const userAns = answers[q.id] ?? (q.type === "checkbox" ? [] : "");
        if (result.mode !== "quiz" || q.type === "text" || q.correctAnswer === undefined || q.correctAnswer === null) {
          return { questionId: q.id, isCorrect: null as boolean | null, userAnswer: userAns, correctAnswer: q.correctAnswer };
        }
        const correct = q.correctAnswer;
        let isCorrect: boolean;
        if (Array.isArray(correct)) {
          const userArr = Array.isArray(userAns) ? userAns : [userAns];
          isCorrect = correct.length === userArr.length && correct.every((c) => userArr.includes(c));
        } else {
          isCorrect = userAns === correct;
        }
        return { questionId: q.id, isCorrect, userAnswer: userAns, correctAnswer: correct };
      });

      setSurveyResult({ ...result, questionResults });
      // Mark item complete after submission
      if (!completedItemIds.includes(surveyItem._id)) {
        await toggleItemComplete(surveyItem._id);
      }
    } catch (err: any) {
      setSurveyError(err?.response?.data?.message || tcd.survey.submitFailed);
    } finally {
      setSurveySubmitting(false);
    }
  };

  const openVideoFullscreen = async () => {
    if (!videoFrameRef.current) return;

    try {
      if (videoFrameRef.current.requestFullscreen) {
        await videoFrameRef.current.requestFullscreen();
      }

      const orientation = (window.screen as any).orientation;
      if (orientation?.lock) {
        await orientation.lock("landscape").catch(() => {});
      }
    } catch {
      // Ignore unsupported fullscreen/orientation lock
    }
  };

  const submitComment = async (content: string, parentCommentId?: string) => {
    if (!id || !content.trim() || postingComment) return;
    try {
      setPostingComment(true);
      const response = await coursesService.createCourseComment(id, {
        content: content.trim(),
        parentCommentId,
      });

      const newComment = response.comment;
      if (parentCommentId) {
        setComments((prev) =>
          prev.map((comment) => {
            if (comment._id !== parentCommentId) return comment;
            const nextReplies = [...(comment.replies || []), newComment];
            return {
              ...comment,
              replies: nextReplies,
              replyCount: nextReplies.length,
            };
          })
        );
        setReplyDrafts((prev) => ({ ...prev, [parentCommentId]: "" }));
        setReplyOpenFor(null);
      } else {
        setComments((prev) => [
          {
            ...newComment,
            replies: [],
            replyCount: 0,
          },
          ...prev,
        ]);
        setCommentInput("");
      }
    } finally {
      setPostingComment(false);
    }
  };

  const toggleLike = async (commentId: string) => {
    if (!id) return;

    setComments((prev) =>
      updateCommentTree(prev, commentId, (comment) => {
        const liked = !comment.likedByMe;
        return {
          ...comment,
          likedByMe: liked,
          likeCount: Math.max(0, comment.likeCount + (liked ? 1 : -1)),
        };
      })
    );

    try {
      const result = await coursesService.toggleCourseCommentLike(id, commentId);
      setComments((prev) =>
        updateCommentTree(prev, commentId, (comment) => ({
          ...comment,
          likedByMe: result.likedByMe,
          likeCount: result.likeCount,
        }))
      );
    } catch {
      setComments((prev) =>
        updateCommentTree(prev, commentId, (comment) => ({
          ...comment,
          likedByMe: !comment.likedByMe,
          likeCount: Math.max(0, comment.likeCount + (comment.likedByMe ? -1 : 1)),
        }))
      );
    }
  };

  const deleteComment = async (commentId: string) => {
    if (!id) return;
    if (!window.confirm(tcd.comments.deleteConfirm)) return;
    try {
      await coursesService.deleteCourseComment(id, commentId);
      setComments((prev) => removeCommentFromTree(prev, commentId));
    } catch {
      // Ignore toast-less failure for now to keep page interaction smooth
    }
  };

  if (loading) {
    return (
      <Box dir={dir} sx={{ backgroundColor: MEMBERSHIP_COLORS.bg, minHeight: "100vh" }}>
          <Container maxWidth="xl" sx={{ py: 4 }}>
            <Skeleton variant="rounded" height={280} />
            <Skeleton variant="text" height={64} sx={{ mt: 2 }} />
            <Skeleton variant="text" width="70%" />
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12, md: 8 }}>
                <Skeleton variant="rounded" height={420} />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Skeleton variant="rounded" height={420} />
              </Grid>
            </Grid>
          </Container>
      </Box>
    );
  }

  if (error || !course) {
    return (
      <Box
          dir={dir}
          sx={{ backgroundColor: MEMBERSHIP_COLORS.bg, minHeight: "100vh", py: 8 }}
        >
        <Container maxWidth="md">
          <Alert severity="error" sx={{ mb: 2 }}>
            {error?.reason === "locked"
              ? tcd.lockedError
              : error?.message || (course ? tcd.loadFailed : tcd.notFound)}
          </Alert>
          <Button
            startIcon={<ArrowBack sx={{ transform: isRTL ? "scaleX(-1)" : "none" }} />}
            variant="outlined"
            onClick={() => navigate("/membership/classes")}
          >
            {tcd.backToCourses}
          </Button>
        </Container>
        </Box>
    );
  }

  return (
    <Box dir={dir} sx={{ backgroundColor: MEMBERSHIP_COLORS.bg, minHeight: "100vh" }}>
      {isLocked && (
        <Container maxWidth="xl" sx={{ pt: 2 }}>
          <Alert severity="warning" sx={{ borderRadius: "10px" }}>
            {tcd.lockedBanner} {lockReason ? tcd.lockedReason(lockReason) : ""}
          </Alert>
        </Container>
      )}

      {/* ── Light Hero ─────────────────────────────────────────── */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          background: "linear-gradient(135deg, #E8F1FB 0%, #F5F8FC 100%)",
          borderBottom: `1px solid ${MEMBERSHIP_COLORS.border}`,
          pt: { xs: 4, md: 6 },
          pb: { xs: 5, md: 6 },
        }}
      >
        {/* Subtle grid */}
        <Box sx={{
          position: "absolute", inset: 0, opacity: 0.55,
          backgroundImage:
            "linear-gradient(rgba(21,101,192,.06) 1px, transparent 1px)," +
            "linear-gradient(90deg, rgba(21,101,192,.06) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
          maskImage: "linear-gradient(180deg, black, transparent 85%)",
          WebkitMaskImage: "linear-gradient(180deg, black, transparent 85%)",
          pointerEvents: "none",
        }} />

        {/* Soft orbs */}
        <Box sx={{
          position: "absolute", top: "-90px", right: "-70px",
          width: 360, height: 360, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(21,101,192,0.18) 0%, transparent 70%)",
          pointerEvents: "none",
        }} />
        <Box sx={{
          position: "absolute", bottom: "-80px", left: "8%",
          width: 280, height: 280, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(66,165,245,0.16) 0%, transparent 70%)",
          pointerEvents: "none",
        }} />

        <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
          <Button
            startIcon={<ArrowBack sx={{ transform: isRTL ? "scaleX(-1)" : "none" }} />}
            onClick={() => navigate("/membership/classes")}
            sx={{
              color: MEMBERSHIP_COLORS.blue,
              borderColor: "rgba(21,101,192,0.35)",
              mb: 2, textTransform: "none",
              bgcolor: "rgba(255,255,255,0.7)",
              backdropFilter: "blur(6px)",
              "&:hover": { bgcolor: "#FFFFFF", borderColor: MEMBERSHIP_COLORS.blue },
            }}
            variant="outlined"
          >
            {tcd.backToCourses}
          </Button>

          <Grid container spacing={3} alignItems="center">
            <Grid size={{ xs: 12, md: 7 }}>
              <Chip
                label={tcd.premiumCourse}
                sx={{
                  mb: 1.5,
                  bgcolor: "rgba(21,101,192,0.10)",
                  color: MEMBERSHIP_COLORS.blue,
                  fontWeight: 700,
                  border: "1px solid rgba(21,101,192,0.25)",
                }}
              />

              <Typography
                variant="h3"
                sx={{
                  fontWeight: 900,
                  letterSpacing: "-0.04em",
                  fontSize: { xs: "1.8rem", md: "3rem" },
                  lineHeight: 1.15,
                  color: MEMBERSHIP_COLORS.navy,
                }}
              >
                {localizedTitle(course, language)}
              </Typography>

              <Typography sx={{ mt: 1.5, maxWidth: 700, lineHeight: 1.8, color: MEMBERSHIP_COLORS.muted, whiteSpace: "pre-line" }}>
                {localized(course.description, course.descriptionAr) || tcd.fallbackDescription}
              </Typography>

              {/* Leaflet guides are sold as a series — nudge towards the rest of it */}
              {course.phases.some((p) => p.items.some((i) => i.type === "pdf")) && (
                <Typography sx={{
                  mt: 1.5, maxWidth: 700, lineHeight: 1.7, fontSize: ".9rem", fontWeight: 600,
                  color: MEMBERSHIP_COLORS.navy,
                  borderInlineStart: `3px solid ${MEMBERSHIP_COLORS.blue}`, paddingInlineStart: 1.5,
                }}>
                  {tcd.seriesNote}
                </Typography>
              )}

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} sx={{ mt: 3, flexWrap: "wrap", rowGap: 1.2 }}>
                {[
                  { icon: <MenuBook sx={{ color: `${MEMBERSHIP_COLORS.blue} !important` }} />, label: tcd.phasesChip(stats?.phaseCount || 0) },
                  { icon: <OndemandVideo sx={{ color: `${MEMBERSHIP_COLORS.blue} !important` }} />, label: tcd.videosChip(stats?.videoCount || 0) },
                  { icon: <Timer sx={{ color: `${MEMBERSHIP_COLORS.blue} !important` }} />, label: formatDuration(stats?.estimatedDuration || 0) },
                  { icon: <CheckCircle sx={{ color: "#10B981 !important" }} />, label: tcd.completedChip(completedCount, trackedItems.length) },
                ].map((chip) => (
                  <Chip
                    key={chip.label}
                    icon={chip.icon}
                    label={chip.label}
                    sx={{
                      bgcolor: "#FFFFFF",
                      color: MEMBERSHIP_COLORS.navy,
                      border: `1px solid ${MEMBERSHIP_COLORS.border}`,
                      fontWeight: 600,
                      boxShadow: "0 1px 4px rgba(10,22,40,0.04)",
                    }}
                  />
                ))}
              </Stack>

              <Box sx={{ mt: 3, maxWidth: 520 }}>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.8 }}>
                  <Typography sx={{ fontSize: "0.85rem", color: MEMBERSHIP_COLORS.muted, fontWeight: 600 }}>
                    {tcd.courseProgress}
                  </Typography>
                  <Typography sx={{ fontSize: "0.85rem", fontWeight: 800, color: MEMBERSHIP_COLORS.blue }}>
                    {progressPct}%
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={progressPct}
                  sx={{
                    height: 10,
                    borderRadius: 30,
                    bgcolor: "rgba(21,101,192,0.12)",
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 30,
                      background: `linear-gradient(90deg, ${MEMBERSHIP_COLORS.blue}, #42A5F5)`,
                    },
                  }}
                />
              </Box>
            </Grid>

            {/* Thumbnail card */}
            <Grid size={{ xs: 12, md: 5 }}>
              <Box sx={{
                position: "relative",
                borderRadius: "20px", overflow: "hidden",
                border: `1px solid ${MEMBERSHIP_COLORS.border}`,
                boxShadow: "0 20px 50px rgba(21,101,192,0.18)",
                aspectRatio: "16 / 10",
                background: course.thumbnail
                  ? `linear-gradient(to top, rgba(10,22,40,0.35), rgba(10,22,40,0)), url(${course.thumbnail}) center/cover no-repeat`
                  : `linear-gradient(135deg, ${MEMBERSHIP_COLORS.blue}, #42A5F5)`,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <Box sx={{
                  width: 72, height: 72, borderRadius: "50%",
                  bgcolor: "rgba(255,255,255,0.92)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
                }}>
                  <PlayCircle sx={{ fontSize: 44, color: MEMBERSHIP_COLORS.blue }} />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <ContentContainer maxWidth="xl" sx={{ py: { xs: 2.5, md: 3.5 } }}>
        <Grid container spacing={{ xs: 2, md: 3 }}>
          <Grid size={{ xs: 12, lg: 8, xl: 9 }}>
            <Paper
              elevation={0}
              sx={{ p: { xs: 2, md: 2.5 }, borderRadius: "20px", border: "1px solid rgba(21,101,192,0.1)", bgcolor: "#FFFFFF" }}
            >
              {/* Header */}
              <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 2.5, flexWrap: "wrap", gap: 1 }}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: MEMBERSHIP_COLORS.navy, letterSpacing: "-.02em" }}>
                    {tcd.curriculum}
                  </Typography>
                  <Typography sx={{ color: MEMBERSHIP_COLORS.muted, fontSize: ".85rem", mt: .3 }}>
                    {tcd.curriculumSubtitle}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1.5} sx={{ flexShrink: 0 }}>
                  {[
                    { label: tcd.phasesSummary(course.phases.length), color: MEMBERSHIP_COLORS.blue },
                    { label: tcd.lessonsSummary(trackedItems.length), color: "#7C3AED" },
                    { label: formatDuration(stats?.estimatedDuration || 0), color: "#00897B" },
                  ].map((s) => (
                    <Typography key={s.label} sx={{ fontSize: ".75rem", fontWeight: 700, color: s.color }}>
                      {s.label}
                    </Typography>
                  ))}
                </Stack>
              </Box>

              {/* Live session */}
              {course.zoomMeeting && (
                <Accordion defaultExpanded disableGutters elevation={0} sx={{ border: "1px solid rgba(200,149,108,0.35)", borderRadius: "14px !important", mb: 1.5, overflow: "hidden", "&:before": { display: "none" } }}>
                  <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: "#FFF8F3", px: 2, minHeight: 56 }}>
                    <Stack direction="row" spacing={1.2} alignItems="center">
                      <Box sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: "rgba(200,149,108,.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Videocam sx={{ color: "#C8956C", fontSize: 18 }} />
                      </Box>
                      <Typography sx={{ fontWeight: 700, color: "#7A5230" }}>{tcd.liveSession}</Typography>
                      <Chip size="small" label={course.zoomMeeting.status === "started" ? tcd.liveNow : tcd.scheduled}
                        sx={{ bgcolor: course.zoomMeeting.status === "started" ? "#EF4444" : "#C8956C", color: "#fff", fontWeight: 700, fontSize: ".65rem", height: 20 }} />
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 2 }}>
                    <Stack spacing={1.5}>
                      <Typography sx={{ fontWeight: 700 }}>{course.zoomMeeting.topic}</Typography>
                      <Typography variant="body2" sx={{ color: MEMBERSHIP_COLORS.muted }}>
                        {new Date(course.zoomMeeting.startTime).toLocaleString(isRTL ? "ar" : undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </Typography>
                      <Button variant="contained" href={course.zoomMeeting.joinUrl} target="_blank" rel="noopener noreferrer"
                        startIcon={<OpenInNew />} sx={{ alignSelf: "flex-start", bgcolor: "#C8956C", "&:hover": { bgcolor: "#B07D55" }, textTransform: "none", borderRadius: "10px", fontWeight: 700 }}>
                        {tcd.joinMeeting}
                      </Button>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Phases */}
              {course.phases
                .slice()
                .sort((a, b) => a.order - b.order)
                .map((phase, phaseIndex) => {
                  const phaseDone = phase.items.filter((i) => completedItemIds.includes(i._id)).length;
                  const phaseTotal = phase.items.length;
                  const phaseComplete = phaseTotal > 0 && phaseDone === phaseTotal;
                  const phasePct = phaseTotal ? Math.round((phaseDone / phaseTotal) * 100) : 0;

                  return (
                    <Accordion
                      key={phase._id}
                      defaultExpanded={phaseIndex === 0}
                      disableGutters elevation={0}
                      sx={{ border: "1px solid rgba(21,101,192,0.1)", borderRadius: "14px !important", mb: 1.5, overflow: "hidden", "&:before": { display: "none" } }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMore sx={{ color: MEMBERSHIP_COLORS.muted }} />}
                        sx={{ px: { xs: 1.5, md: 2 }, py: 0, minHeight: { xs: 60, md: 64 }, bgcolor: "#FAFCFF",
                          "& .MuiAccordionSummary-content": { my: 0, alignItems: "center" } }}
                      >
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: "100%", pr: 1 }}>
                          {/* Phase badge */}
                          <Box sx={{
                            width: 36, height: 36, borderRadius: "10px", flexShrink: 0,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            bgcolor: phaseComplete ? "#10B981" : MEMBERSHIP_COLORS.blue,
                            boxShadow: phaseComplete ? "0 4px 12px rgba(16,185,129,.3)" : "0 4px 12px rgba(21,101,192,.25)",
                          }}>
                            {phaseComplete
                              ? <CheckCircle sx={{ color: "#fff", fontSize: 20 }} />
                              : <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: ".85rem" }}>
                                  {String(phaseIndex + 1).padStart(2, "0")}
                                </Typography>
                            }
                          </Box>

                          {/* Title + progress */}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: .4 }}>
                              <Typography sx={{ fontWeight: 700, fontSize: ".95rem", color: MEMBERSHIP_COLORS.navy, lineHeight: 1.2 }} noWrap>
                                {localizedTitle(phase, language)}
                              </Typography>
                              <Chip
                                label={`${phaseDone}/${phaseTotal}`}
                                size="small"
                                sx={{
                                  height: 20, fontSize: ".68rem", fontWeight: 700,
                                  bgcolor: phaseComplete ? "rgba(16,185,129,.12)" : "rgba(21,101,192,.1)",
                                  color: phaseComplete ? "#10B981" : MEMBERSHIP_COLORS.blue,
                                  flexShrink: 0,
                                }}
                              />
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Box sx={{ flex: 1, height: 4, borderRadius: 99, bgcolor: "rgba(21,101,192,.1)", overflow: "hidden" }}>
                                <Box sx={{
                                  height: "100%", borderRadius: 99,
                                  width: `${phasePct}%`,
                                  bgcolor: phaseComplete ? "#10B981" : MEMBERSHIP_COLORS.blue,
                                  transition: "width .8s ease",
                                }} />
                              </Box>
                              <Typography sx={{ fontSize: ".7rem", fontWeight: 700, color: phaseComplete ? "#10B981" : MEMBERSHIP_COLORS.muted, flexShrink: 0 }}>
                                {phasePct}%
                              </Typography>
                            </Stack>
                          </Box>
                        </Stack>
                      </AccordionSummary>

                      <AccordionDetails sx={{ p: { xs: 1, md: 1.5 }, pt: 0 }}>
                        <Box sx={{ borderTop: "1px solid rgba(21,101,192,.07)", pt: 1 }}>
                          {phase.items
                            .slice()
                            .sort((a, b) => a.order - b.order)
                            .map((item, itemIndex) => {
                              const isDone = completedItemIds.includes(item._id);
                              const isNext = nextLearningItem?.item._id === item._id;
                              const isVideo  = item.type === "video";
                              const isPdf    = item.type === "pdf";
                              const isLetter = item.type === "letter";
                              const isSurvey = item.type === "survey";
                              const canOpen  = hasReadableContent(item);
                              const surveyMode = isSurvey ? (item.surveyConfig?.mode ?? "survey") : null;
                              // Leaflets can carry a paid companion video. Show it either way:
                              // playable when the add-on is owned, flagged as locked when not.
                              const companion = isPdf ? item.companionVideo || null : null;
                              const companionPlayback = resolveVideoPlayback(companion);
                              const companionPlayable = companionPlayback.kind !== "none";

                              // Icon config per type
                              const typeIcon = isVideo
                                ? <PlayCircle sx={{ fontSize: 16 }} />
                                : isPdf
                                ? <Description sx={{ fontSize: 16 }} />
                                : isLetter
                                ? <TextSnippet sx={{ fontSize: 16 }} />
                                : <Quiz sx={{ fontSize: 16 }} />;
                              const typeColor  = isVideo ? MEMBERSHIP_COLORS.blue : isPdf ? "#EF4444" : isLetter ? "#00897B" : "#7C3AED";
                              const typeLabel  = isVideo
                                ? tcd.itemTypes.video
                                : isPdf
                                ? tcd.itemTypes.pdf
                                : isLetter
                                ? tcd.itemTypes.letter
                                : surveyMode === "quiz" ? tcd.itemTypes.quiz : tcd.itemTypes.survey;
                              const typeMeta   = isVideo
                                ? item.video?.duration ? formatDuration(item.video.duration) : null
                                : isPdf
                                ? item.pdfPages ? tcd.pagesMeta(item.pdfPages) : null
                                : isSurvey
                                ? item.surveyConfig?.questions?.length ? tcd.questionsMeta(item.surveyConfig.questions.length) : null
                                : null;

                              return (
                                <Box
                                  key={item._id}
                                  onClick={() => !isLocked && canOpen && openCourseItem(item)}
                                  sx={{
                                    // Mobile: title row wraps, action buttons drop to a second
                                    // line so the title isn't squeezed into an ellipsis.
                                    display: "flex", alignItems: "center",
                                    flexWrap: { xs: "wrap", md: "nowrap" },
                                    columnGap: { xs: 1.2, md: 1.5 }, rowGap: 1,
                                    px: { xs: 1, md: 1.5 }, py: 1.2,
                                    borderRadius: "10px", mb: .5, cursor: canOpen && !isLocked ? "pointer" : "default",
                                    bgcolor: isNext ? "rgba(21,101,192,.04)" : isDone ? "rgba(16,185,129,.03)" : "transparent",
                                    border: isNext ? "1px solid rgba(21,101,192,.15)" : "1px solid transparent",
                                    transition: "all .15s ease",
                                    "&:hover": canOpen && !isLocked ? {
                                      bgcolor: "rgba(21,101,192,.06)",
                                      border: "1px solid rgba(21,101,192,.18)",
                                    } : {},
                                  }}
                                >
                                  {/* Item number */}
                                  <Typography sx={{ fontSize: ".72rem", fontWeight: 700, color: MEMBERSHIP_COLORS.muted, minWidth: 22, flexShrink: 0 }}>
                                    {String(itemIndex + 1).padStart(2, "0")}
                                  </Typography>

                                  {/* Type icon badge */}
                                  <Box sx={{
                                    width: 32, height: 32, borderRadius: "8px", flexShrink: 0,
                                    bgcolor: `${typeColor}15`,
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    color: typeColor,
                                  }}>
                                    {typeIcon}
                                  </Box>

                                  {/* Title + meta */}
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ rowGap: .4 }}>
                                      <Typography sx={{
                                        fontWeight: 600, fontSize: { xs: ".9rem", md: ".95rem" },
                                        color: isDone ? MEMBERSHIP_COLORS.muted : MEMBERSHIP_COLORS.navy,
                                        textDecoration: isDone ? "line-through" : "none",
                                        lineHeight: 1.3,
                                        // Up to two lines on mobile, single ellipsised line on desktop
                                        overflow: "hidden", textOverflow: "ellipsis",
                                        display: { xs: "-webkit-box", md: "block" },
                                        WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                                        whiteSpace: { xs: "normal", md: "nowrap" },
                                      }}>
                                        {localizedTitle(item, language)}
                                      </Typography>
                                      {isNext && (
                                        <Chip label={tcd.nowPlaying} size="small"
                                          sx={{ height: 18, fontSize: ".6rem", fontWeight: 800, bgcolor: MEMBERSHIP_COLORS.blue, color: "#fff", letterSpacing: ".05em" }} />
                                      )}
                                    </Stack>
                                    {/* Each meta segment is its own inline group so it wraps as a unit */}
                                    <Stack direction="row" spacing={.6} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: .25, rowGap: .2 }}>
                                      <Typography sx={{ fontSize: ".72rem", color: MEMBERSHIP_COLORS.muted, textTransform: "capitalize", whiteSpace: "nowrap" }}>
                                        {typeLabel}
                                      </Typography>
                                      {typeMeta && (
                                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: .6, whiteSpace: "nowrap" }}>
                                          <Typography sx={{ fontSize: ".6rem", color: MEMBERSHIP_COLORS.muted }}>·</Typography>
                                          <Timer sx={{ fontSize: 11, color: MEMBERSHIP_COLORS.muted }} />
                                          <Typography sx={{ fontSize: ".72rem", color: MEMBERSHIP_COLORS.muted }}>{typeMeta}</Typography>
                                        </Box>
                                      )}
                                      {companion && (
                                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: .6, whiteSpace: "nowrap" }}>
                                          <Typography sx={{ fontSize: ".6rem", color: MEMBERSHIP_COLORS.muted }}>·</Typography>
                                          {companion.locked
                                            ? <LockOutlined sx={{ fontSize: 11, color: MEMBERSHIP_COLORS.muted }} />
                                            : <OndemandVideo sx={{ fontSize: 12, color: MEMBERSHIP_COLORS.blue }} />}
                                          <Typography sx={{
                                            fontSize: ".72rem", fontWeight: 700,
                                            color: companion.locked ? MEMBERSHIP_COLORS.muted : MEMBERSHIP_COLORS.blue,
                                          }}>
                                            {tcd.companion.label}
                                            {companion.duration ? ` · ${formatDuration(companion.duration)}` : ""}
                                          </Typography>
                                        </Box>
                                      )}
                                    </Stack>
                                  </Box>

                                  {/* Mark done / undo toggle — stays on the title row at every width */}
                                  <Box
                                    onClick={(e) => { e.stopPropagation(); if (!isLocked && !savingProgress) toggleItemComplete(item._id); }}
                                    title={isDone ? tcd.markIncomplete : tcd.markComplete}
                                    sx={{
                                      cursor: isLocked || savingProgress ? "default" : "pointer",
                                      width: 32, height: 32, borderRadius: "50%",
                                      display: "flex", alignItems: "center", justifyContent: "center",
                                      flexShrink: 0,
                                      order: { xs: 0, md: 1 },
                                      border: isDone ? "none" : "2px solid rgba(0,0,0,.15)",
                                      bgcolor: isDone ? "rgba(16,185,129,.12)" : "transparent",
                                      transition: "all .2s ease",
                                      "&:hover": isLocked || savingProgress ? {} : {
                                        bgcolor: isDone ? "rgba(16,185,129,.22)" : "rgba(16,185,129,.08)",
                                        borderColor: "#10B981",
                                      },
                                    }}
                                  >
                                    {savingProgress
                                      ? <CircularProgress size={14} sx={{ color: MEMBERSHIP_COLORS.muted }} />
                                      : isDone
                                      ? <CheckCircle sx={{ color: "#10B981", fontSize: 20 }} />
                                      : <RadioButtonUnchecked sx={{ color: "rgba(0,0,0,.2)", fontSize: 20 }} />
                                    }
                                  </Box>

                                  {/* Action buttons — inline on desktop, own full-width line on mobile */}
                                  {(companion || canOpen) && (
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    flexWrap="wrap"
                                    useFlexGap
                                    sx={{
                                      flexShrink: 0,
                                      order: { xs: 1, md: 0 },
                                      flexBasis: { xs: "100%", md: "auto" },
                                      ml: { md: "auto" },
                                      // 22px number + 32px icon + two 1.2-spacing gaps → lines up under the title
                                      pl: { xs: "73px", md: 0 },
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {/* Companion video — paid add-on on PDF leaflets */}
                                    {companion && (
                                      <Tooltip
                                        title={
                                          companion.locked
                                            ? (canBuyVideo
                                                ? tcd.companion.buyHint(VIDEO_ADDON_PER_COURSE)
                                                : tcd.companion.lockedHint)
                                          : companionPlayable ? ""
                                          : tcd.companion.processing
                                        }
                                      >
                                        <span>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            // A locked video that can be bought stays clickable — it
                                            // leads to checkout instead of the player.
                                            disabled={isLocked || (companion.locked ? !canBuyVideo : !companionPlayable)}
                                            onClick={() =>
                                              companion.locked
                                                ? buyCompanionVideo()
                                                : openCompanionVideo(companion, localizedTitle(item, language))
                                            }
                                            startIcon={
                                              companion.locked
                                                ? <LockOutlined sx={{ fontSize: "14px !important" }} />
                                                : <PlayCircle sx={{ fontSize: "15px !important" }} />
                                            }
                                            sx={{
                                              textTransform: "none", fontWeight: 700,
                                              fontSize: ".78rem", borderRadius: "20px",
                                              px: 1.6, py: .5, minWidth: 0, whiteSpace: "nowrap",
                                              borderColor: "rgba(21,101,192,.35)",
                                              color: MEMBERSHIP_COLORS.blue,
                                              "&:hover": { borderColor: MEMBERSHIP_COLORS.blue, bgcolor: "rgba(21,101,192,.06)" },
                                              "&.Mui-disabled": { borderColor: "rgba(0,0,0,.12)", color: "rgba(0,0,0,.3)" },
                                            }}
                                          >
                                            {companion.locked
                                              ? (canBuyVideo ? tcd.companion.buy(VIDEO_ADDON_PER_COURSE) : tcd.companion.locked)
                                              : tcd.companion.watch}
                                          </Button>
                                        </span>
                                      </Tooltip>
                                    )}

                                    {/* Open / Watch / Read action */}
                                    {canOpen && (
                                      <Button
                                        size="small"
                                        variant={isDone ? "outlined" : "contained"}
                                        disabled={isLocked}
                                        onClick={() => openCourseItem(item)}
                                        endIcon={<ArrowForward sx={{ fontSize: "13px !important", transform: isRTL ? "scaleX(-1)" : "none" }} />}
                                        sx={{
                                          textTransform: "none", fontWeight: 700,
                                          fontSize: ".78rem", borderRadius: "20px",
                                          px: 1.8, py: .55, whiteSpace: "nowrap",
                                          ...(isDone
                                            ? {
                                                borderColor: "rgba(0,0,0,.15)",
                                                color: MEMBERSHIP_COLORS.muted,
                                                bgcolor: "transparent",
                                                "&:hover": { borderColor: MEMBERSHIP_COLORS.blue, color: MEMBERSHIP_COLORS.blue, bgcolor: "rgba(21,101,192,.04)" },
                                              }
                                            : {
                                                bgcolor: isSurvey ? "#7C3AED" : MEMBERSHIP_COLORS.blue,
                                                boxShadow: "none",
                                                "&:hover": { bgcolor: isSurvey ? "#6D28D9" : MEMBERSHIP_COLORS.navy, boxShadow: "none" },
                                              }
                                          ),
                                          "&.Mui-disabled": { bgcolor: "rgba(0,0,0,.05)", color: "rgba(0,0,0,.28)", borderColor: "transparent" },
                                        }}
                                      >
                                        {isVideo
                                          ? (isNext ? tcd.actions.resume : tcd.actions.watch)
                                          : isPdf ? tcd.actions.open
                                          : isLetter ? tcd.actions.read
                                          : surveyMode === "quiz" ? tcd.actions.startQuiz : tcd.actions.takeSurvey}
                                      </Button>
                                    )}
                                  </Stack>
                                  )}
                                </Box>
                              );
                          })}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  );
              })}
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, lg: 4, xl: 3 }}>
            <Paper
              elevation={0}
              sx={{
                p: 2.2,
                borderRadius: "18px",
                border: "1px solid rgba(0,0,0,0.07)",
                bgcolor: "#FFFFFF",
                position: { xl: "sticky" },
                top: 95,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 1.2 }}>
                {tcd.continueLearning}
              </Typography>
              <Typography sx={{ color: MEMBERSHIP_COLORS.secondary, mb: 2 }}>
                {tcd.continueSubtitle}
              </Typography>

              <Divider sx={{ mb: 2 }} />

              <Stack spacing={1} sx={{ mb: 2.2 }}>
                <Typography variant="body2">
                  {tcd.totalLessons}: {stats?.itemCount || 0}
                </Typography>
                <Typography variant="body2">
                  {tcd.completedLabel}: {completedCount}
                </Typography>
                <Typography variant="body2">
                  {tcd.estimatedTime}: {formatDuration(stats?.estimatedDuration || 0)}
                </Typography>
              </Stack>

              <Button
                fullWidth
                variant="contained"
                disabled={!nextLearningItem || isLocked}
                onClick={() => {
                  if (!nextLearningItem) return;
                  openCourseItem(nextLearningItem.item);
                }}
                sx={{
                  textTransform: "none",
                  borderRadius: "12px",
                  fontWeight: 700,
                  py: 1.2,
                  backgroundColor: MEMBERSHIP_COLORS.accent,
                  "&:hover": { backgroundColor: "#C88F70" },
                }}
              >
                {continueLabel}
              </Button>
            </Paper>
          </Grid>
        </Grid>

        <Paper
          elevation={0}
          sx={{
            mt: 3,
            p: { xs: 1.4, md: 2.2 },
            borderRadius: "18px",
            border: "1px solid rgba(0,0,0,0.07)",
            bgcolor: "#FFFFFF",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.8 }}>
            {tcd.comments.heading}
          </Typography>
          <Typography sx={{ color: MEMBERSHIP_COLORS.secondary, mb: 2 }}>
            {tcd.comments.subtitle}
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: { xs: 1, md: 1.2 },
              alignItems: { xs: "flex-start", md: "center" },
              mb: 2,
            }}
          >
            <Avatar src={user?.avatar} sx={{ width: 36, height: 36 }}>
              {user?.name?.[0]?.toUpperCase() || "U"}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={5}
                placeholder={tcd.comments.placeholder}
                value={commentInput}
                onChange={(e) => setCommentInput(e.target.value)}
                inputProps={{ maxLength: 1000 }}
              />
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1 }}>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {commentInput.length}/1000
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Send />}
                  disabled={!commentInput.trim() || postingComment}
                  onClick={() => submitComment(commentInput)}
                  sx={{
                    textTransform: "none",
                    borderRadius: "10px",
                    backgroundColor: MEMBERSHIP_COLORS.accent,
                    "&:hover": { backgroundColor: "#C88F70" },
                  }}
                >
                  {tcd.comments.post}
                </Button>
              </Stack>
            </Box>
          </Box>

          <Divider sx={{ mb: 1.5 }} />

          {commentsLoading ? (
            <Stack spacing={1}>
              <Skeleton variant="rounded" height={90} />
              <Skeleton variant="rounded" height={90} />
            </Stack>
          ) : comments.length === 0 ? (
            <Alert severity="info">{tcd.comments.empty}</Alert>
          ) : (
            <Stack spacing={1.3}>
              {comments.map((comment) => {
                const canDelete = user?._id === comment.user._id || user?.type === "admin";
                return (
                  <Box key={comment._id}>
                    <Box
                      sx={{
                        p: 1.2,
                        borderRadius: "12px",
                        border: "1px solid rgba(0,0,0,0.06)",
                        bgcolor: "#FCFCFD",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <Avatar src={comment.user.avatar} sx={{ width: 34, height: 34 }}>
                          {comment.user.name?.[0]?.toUpperCase() || "U"}
                        </Avatar>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={{ xs: 0.3, sm: 1 }}
                            alignItems={{ xs: "flex-start", sm: "center" }}
                          >
                            <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                              {comment.user.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                              {formatCommentDate(comment.createdAt, language)}
                            </Typography>
                          </Stack>
                          <Typography
                            sx={{
                              mt: 0.6,
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                            }}
                          >
                            {comment.content}
                          </Typography>

                          <Stack direction="row" spacing={0.5} sx={{ mt: 0.8 }}>
                            <Button
                              size="small"
                              startIcon={comment.likedByMe ? <ThumbUp /> : <ThumbUpOffAlt />}
                              onClick={() => toggleLike(comment._id)}
                              sx={{ textTransform: "none", minWidth: "auto", px: 1 }}
                            >
                              {comment.likeCount}
                            </Button>
                            <Button
                              size="small"
                              startIcon={<Reply />}
                              onClick={() =>
                                setReplyOpenFor(replyOpenFor === comment._id ? null : comment._id)
                              }
                              sx={{ textTransform: "none", minWidth: "auto", px: 1 }}
                            >
                              {tcd.comments.reply}
                            </Button>
                            {canDelete && (
                              <IconButton size="small" onClick={() => deleteComment(comment._id)}>
                                <DeleteOutline fontSize="small" />
                              </IconButton>
                            )}
                          </Stack>

                          {replyOpenFor === comment._id && (
                            <Box sx={{ mt: 1 }}>
                              <TextField
                                fullWidth
                                size="small"
                                multiline
                                minRows={2}
                                maxRows={4}
                                placeholder={tcd.comments.replyTo(comment.user.name)}
                                value={replyDrafts[comment._id] || ""}
                                onChange={(e) =>
                                  setReplyDrafts((prev) => ({
                                    ...prev,
                                    [comment._id]: e.target.value,
                                  }))
                                }
                                inputProps={{ maxLength: 1000 }}
                              />
                              <Stack direction="row" justifyContent="flex-end" sx={{ mt: 0.8 }}>
                                <Button
                                  size="small"
                                  variant="contained"
                                  startIcon={<Send />}
                                  disabled={!replyDrafts[comment._id]?.trim() || postingComment}
                                  onClick={() =>
                                    submitComment(replyDrafts[comment._id] || "", comment._id)
                                  }
                                  sx={{ textTransform: "none", borderRadius: "8px" }}
                                >
                                  {tcd.comments.reply}
                                </Button>
                              </Stack>
                            </Box>
                          )}

                          {(comment.replies || []).length > 0 && (
                            <Stack spacing={0.8} sx={{ mt: 1.2 }}>
                              {(comment.replies || []).map((reply) => {
                                const canDeleteReply =
                                  user?._id === reply.user._id || user?.type === "admin";
                                return (
                                  <Box
                                    key={reply._id}
                                    sx={{
                                      ml: { xs: 0, sm: 1.5 },
                                      pl: 1,
                                      borderInlineStart: "2px solid rgba(0,0,0,0.08)",
                                    }}
                                  >
                                    <Stack direction="row" spacing={1} alignItems="flex-start">
                                      <Avatar src={reply.user.avatar} sx={{ width: 28, height: 28 }}>
                                        {reply.user.name?.[0]?.toUpperCase() || "U"}
                                      </Avatar>
                                      <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                            {reply.user.name}
                                          </Typography>
                                          <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                            {formatCommentDate(reply.createdAt, language)}
                                          </Typography>
                                        </Stack>
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            mt: 0.4,
                                            whiteSpace: "pre-wrap",
                                            wordBreak: "break-word",
                                          }}
                                        >
                                          {reply.content}
                                        </Typography>
                                        <Stack direction="row" spacing={0.4} sx={{ mt: 0.4 }}>
                                          <Button
                                            size="small"
                                            startIcon={reply.likedByMe ? <ThumbUp /> : <ThumbUpOffAlt />}
                                            onClick={() => toggleLike(reply._id)}
                                            sx={{ textTransform: "none", minWidth: "auto", px: 1 }}
                                          >
                                            {reply.likeCount}
                                          </Button>
                                          {canDeleteReply && (
                                            <IconButton
                                              size="small"
                                              onClick={() => deleteComment(reply._id)}
                                            >
                                              <DeleteOutline fontSize="small" />
                                            </IconButton>
                                          )}
                                        </Stack>
                                      </Box>
                                    </Stack>
                                  </Box>
                                );
                              })}
                            </Stack>
                          )}
                        </Box>
                      </Stack>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Paper>
      </ContentContainer>

      <Dialog
        open={!!lessonPreview}
        onClose={() => setLessonPreview(null)}
        fullScreen={isMobile}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : "14px",
            overflow: "hidden",
            m: isMobile ? 0 : undefined,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 1.2,
            px: isMobile ? 1.2 : 2,
          }}
        >
          <Typography variant="subtitle1" fontWeight={700} noWrap>
            {lessonPreview?.title || tcd.viewer.lessonFallbackTitle}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              onClick={openVideoFullscreen}
              startIcon={<Fullscreen />}
              sx={{
                textTransform: "none",
                minWidth: isMobile ? "auto" : undefined,
                px: isMobile ? 1 : 1.5,
              }}
            >
              {isMobile ? "" : tcd.viewer.fullscreen}
            </Button>
            <Button
              onClick={() => setLessonPreview(null)}
              startIcon={<Close />}
              sx={{
                textTransform: "none",
                minWidth: isMobile ? "auto" : undefined,
                px: isMobile ? 1 : 1.5,
              }}
            >
              {isMobile ? "" : tcd.viewer.close}
            </Button>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: "#000" }}>
          {isMobile && (
            <Box
              sx={{
                px: 1.5,
                py: 1,
                bgcolor: "rgba(255,255,255,0.08)",
                borderBottom: "1px solid rgba(255,255,255,0.12)",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <ScreenRotation sx={{ color: "#FFFFFF", fontSize: 18 }} />
              <Typography
                sx={{ color: "#FFFFFF", fontSize: "0.82rem", opacity: 0.9 }}
              >
                {tcd.viewer.rotateHint}
              </Typography>
            </Box>
          )}
          {lessonPreview && lessonPreview.playback.kind !== "none" ? (
            <Box
              ref={videoFrameRef}
              sx={{
                width: "100%",
                aspectRatio: isMobile ? "9/16" : "16/9",
                maxHeight: isMobile ? "calc(100vh - 64px)" : "none",
                bgcolor: "#000",
                mx: "auto",
              }}
            >
              <VideoPlayer
                playback={lessonPreview.playback}
                title={lessonPreview.title}
                poster={lessonPreview.poster}
              />
            </Box>
          ) : (
            <Alert severity="warning" sx={{ m: 2 }}>
              {tcd.viewer.videoUnavailable}
            </Alert>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!contentPreview}
        onClose={() => setContentPreview(null)}
        fullScreen={isMobile}
        fullWidth
        maxWidth={contentPreview?.type === "pdf" ? "lg" : "md"}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : "14px",
            overflow: "hidden",
            m: isMobile ? 0 : undefined,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 1.2,
            px: isMobile ? 1.2 : 2,
            gap: 1,
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              {contentPreview?.title || tcd.viewer.contentFallbackTitle}
            </Typography>
            {contentPreview?.type === "pdf" && (
              <Typography variant="caption" color="text.secondary">
                {tcd.viewer.pdfLabel} · {contentPreview.pdfPages ? `${tcd.pagesMeta(contentPreview.pdfPages)} · ` : ""}
                {pdfViewLang === "ar" && contentPreview.pdfUrlAr ? tcd.viewer.arabicVersion : tcd.viewer.englishVersion}
              </Typography>
            )}
          </Box>

          {/* Language pills — only for PDF items */}
          {contentPreview?.type === "pdf" && (
            <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
              {(["en", "ar"] as const).map((lang) => {
                const hasVersion = lang === "en" ? !!contentPreview.pdfUrl : !!contentPreview.pdfUrlAr;
                const isActive = pdfViewLang === lang;
                return (
                  <Box
                    key={lang}
                    onClick={() => hasVersion && setPdfViewLang(lang)}
                    sx={{
                      px: 1.2, py: 0.4, borderRadius: "20px", cursor: hasVersion ? "pointer" : "default",
                      fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.04em",
                      border: `1.5px solid ${isActive ? "#1565C0" : hasVersion ? "rgba(0,0,0,0.15)" : "rgba(0,0,0,0.07)"}`,
                      bgcolor: isActive ? "#1565C0" : "transparent",
                      color: isActive ? "#fff" : hasVersion ? "#546E7A" : "rgba(0,0,0,0.25)",
                      transition: "all 0.15s",
                      "&:hover": hasVersion && !isActive ? { borderColor: "#1565C0", color: "#1565C0" } : {},
                      position: "relative",
                    }}
                  >
                    {lang.toUpperCase()}
                    {!hasVersion && (
                      <Box component="span" sx={{ fontSize: "0.6rem", ml: 0.3, opacity: 0.6 }}>—</Box>
                    )}
                  </Box>
                );
              })}
            </Stack>
          )}

          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            {/* Jump straight to the paid companion video from the leaflet */}
            {contentPreview?.type === "pdf" && contentPreview.companion && (
              <Tooltip
                title={
                  contentPreview.companion.locked
                    ? (canBuyVideo
                        ? tcd.companion.buyHint(VIDEO_ADDON_PER_COURSE)
                        : tcd.companion.lockedHint)
                  : isPlayable(contentPreview.companion) ? ""
                  : tcd.companion.processing
                }
              >
                <span>
                  <Button
                    variant="contained"
                    disableElevation
                    disabled={
                      contentPreview.companion.locked
                        ? !canBuyVideo
                        : !isPlayable(contentPreview.companion)
                    }
                    onClick={() =>
                      contentPreview.companion?.locked
                        ? buyCompanionVideo()
                        : openCompanionVideo(
                            contentPreview.type === "pdf" ? contentPreview.companion : null,
                            contentPreview.title
                          )
                    }
                    startIcon={
                      contentPreview.companion.locked
                        ? <LockOutlined sx={{ fontSize: "16px !important" }} />
                        : <PlayCircle sx={{ fontSize: "17px !important" }} />
                    }
                    sx={{
                      textTransform: "none", fontWeight: 700, borderRadius: "20px",
                      minWidth: isMobile ? "auto" : undefined, px: isMobile ? 1 : 1.6,
                      bgcolor: MEMBERSHIP_COLORS.blue,
                      "& .MuiButton-startIcon": isMobile ? { m: 0 } : {},
                      "&:hover": { bgcolor: MEMBERSHIP_COLORS.navy },
                    }}
                  >
                    {isMobile
                      ? ""
                      : contentPreview.companion.locked
                      ? (canBuyVideo ? tcd.companion.buy(VIDEO_ADDON_PER_COURSE) : tcd.companion.locked)
                      : tcd.companion.watch}
                  </Button>
                </span>
              </Tooltip>
            )}
            {contentPreview?.type === "pdf" && (
              <Button
                onClick={async () => {
                  const effectiveKey = pdfViewLang === "ar" && contentPreview.pdfUrlAr
                    ? contentPreview.pdfUrlAr
                    : contentPreview.pdfUrl || contentPreview.pdfUrlAr;
                  if (!effectiveKey) return;
                  try {
                    const res = await coursesService.getPdfSignedUrl(effectiveKey, "view");
                    window.open(res.url, "_blank", "noopener,noreferrer");
                  } catch {
                    window.open(effectiveKey, "_blank", "noopener,noreferrer");
                  }
                }}
                startIcon={<OpenInNew />}
                sx={{ textTransform: "none", minWidth: isMobile ? "auto" : undefined, px: isMobile ? 1 : 1.5 }}
              >
                {isMobile ? "" : tcd.viewer.open}
              </Button>
            )}
            <Button
              onClick={() => setContentPreview(null)}
              startIcon={<Close />}
              sx={{ textTransform: "none", minWidth: isMobile ? "auto" : undefined, px: isMobile ? 1 : 1.5 }}
            >
              {isMobile ? "" : tcd.viewer.close}
            </Button>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ p: 0, bgcolor: contentPreview?.type === "pdf" ? "#111" : "#FAFAFA" }}>
          {contentPreview?.type === "pdf" ? (
            <Box sx={{ height: isMobile ? "calc(100vh - 120px)" : "80vh" }}>
              <MemberPdfViewer
                pdfKey={
                  (pdfViewLang === "ar"
                    ? (contentPreview.pdfUrlAr || contentPreview.pdfUrl)
                    : (contentPreview.pdfUrl || contentPreview.pdfUrlAr)) ?? ""
                }
                title={contentPreview.title}
              />
            </Box>
          ) : contentPreview?.type === "letter" ? (
            <Box
              sx={{
                p: { xs: 1.5, md: 3 },
                height: isMobile ? "calc(100vh - 120px)" : "72vh",
                overflowY: "auto",
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.4, md: 2.2 },
                  borderRadius: "12px",
                  border: "1px solid rgba(0,0,0,0.08)",
                  bgcolor: "#FFFFFF",
                }}
              >
                <Box
                  dir={dir}
                  sx={{
                    lineHeight: 1.85,
                    color: MEMBERSHIP_COLORS.text,
                    fontSize: { xs: "0.98rem", md: "1.02rem" },
                    maxWidth: "78ch",
                    mx: "auto",
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                    hyphens: "auto",
                    "& p": { mt: 0, mb: 1.2 },
                    "& p:last-child": { mb: 0 },
                    "& ul, & ol": { pl: 2.4, mt: 0, mb: 1.2 },
                    "& li": { mb: 0.4 },
                    "& a": { color: "#2563EB", textDecoration: "underline" },
                    "& h1, & h2, & h3": { lineHeight: 1.4, mb: 1 },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: sanitizeLetterHtml(contentPreview.letterContent || ""),
                  }}
                />
              </Paper>
            </Box>
          ) : null}
        </DialogContent>
      </Dialog>
      {/* ─── Survey / Quiz Dialog ─────────────────────────────── */}
      <Dialog
        open={surveyOpen}
        onClose={() => { if (!surveySubmitting) setSurveyOpen(false); }}
        fullScreen={isMobile}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : "18px",
            m: isMobile ? 0 : undefined,
            height: isMobile ? "100%" : "auto",
            maxHeight: isMobile ? "100%" : "90vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Dialog Header */}
        <DialogTitle
          sx={{
            px: { xs: 2, md: 3 },
            py: { xs: 1.5, md: 2 },
            borderBottom: "1px solid rgba(0,0,0,0.08)",
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 38, height: 38, borderRadius: "10px",
                bgcolor: "#F5F3FF",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Quiz sx={{ color: "#7C3AED", fontSize: 22 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                fontWeight={800}
                noWrap
                sx={{ color: "#1F1714", lineHeight: 1.2 }}
              >
                {localizedTitle(surveyItem, language) || tcd.survey.fallbackTitle}
              </Typography>
              <Typography variant="caption" sx={{ color: "#7A6E64" }}>
                {surveyItem?.surveyConfig?.mode === "quiz" ? tcd.survey.gradedQuiz : tcd.survey.survey}
                {" · "}
                {tcd.survey.questionCount(surveyItem?.surveyConfig?.questions?.length ?? 0)}
              </Typography>
            </Box>
            {!surveySubmitting && (
              <IconButton
                size="small"
                onClick={() => setSurveyOpen(false)}
                sx={{ flexShrink: 0, color: "#9CA3AF" }}
              >
                <Close />
              </IconButton>
            )}
          </Box>
        </DialogTitle>

        {/* Dialog Body — scrollable */}
        <DialogContent
          sx={{
            px: { xs: 2, md: 3 },
            py: { xs: 2, md: 2.5 },
            flex: 1,
            overflowY: "auto",
          }}
        >
          {surveyResult ? (
            /* ─── Result Screen ─── */
            <>
              {surveyResult.mode === "quiz" ? (
                /* Quiz result: score summary + per-question review */
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: { xs: 2, md: 3 } }}>
                  {/* Score summary — centered */}
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 2 }}>
                    <Box
                      sx={{
                        width: 80, height: 80, borderRadius: "50%",
                        bgcolor: surveyResult.passed ? "#ECFDF5" : "#FEF2F2",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}
                    >
                      {surveyResult.passed
                        ? <EmojiEvents sx={{ fontSize: 44, color: "#10B981" }} />
                        : <SentimentDissatisfied sx={{ fontSize: 44, color: "#EF4444" }} />}
                    </Box>
                    <Typography variant="h4" fontWeight={900} sx={{ color: surveyResult.passed ? "#10B981" : "#EF4444" }}>
                      {surveyResult.score}%
                    </Typography>
                    {surveyResult.totalQuestions !== undefined && (
                      <Typography variant="body2" sx={{ color: "text.secondary", mt: -1 }}>
                        {tcd.survey.correctOf(surveyResult.correctAnswers ?? 0, surveyResult.totalQuestions)}
                      </Typography>
                    )}
                    <Chip
                      label={surveyResult.passed ? tcd.survey.passed : tcd.survey.notPassed}
                      sx={{
                        fontWeight: 700, fontSize: "1rem", px: 2, height: 36,
                        bgcolor: surveyResult.passed ? "#ECFDF5" : "#FEF2F2",
                        color: surveyResult.passed ? "#10B981" : "#EF4444",
                      }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 340 }}>
                      {surveyResult.passed
                        ? tcd.survey.passedBody
                        : tcd.survey.failedBody(surveyItem?.surveyConfig?.passingScore ?? 60)}
                    </Typography>
                  </Box>

                  {/* Per-question answer review */}
                  {surveyResult.questionResults && surveyResult.questionResults.length > 0 && (
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5, color: "#1F1714" }}>
                        {tcd.survey.reviewAnswers}
                      </Typography>
                      <Stack spacing={1.5}>
                        {surveyResult.questionResults.map((qr, idx) => {
                          const question = surveyItem?.surveyConfig?.questions.find((q) => q.id === qr.questionId);
                          const userAnswerDisplay = Array.isArray(qr.userAnswer)
                            ? qr.userAnswer.length > 0 ? qr.userAnswer.join(listSeparator) : tcd.survey.noAnswer
                            : String(qr.userAnswer || tcd.survey.noAnswer);
                          const correctAnswerDisplay = qr.correctAnswer
                            ? Array.isArray(qr.correctAnswer) ? qr.correctAnswer.join(", ") : String(qr.correctAnswer)
                            : null;
                          const borderColor = qr.isCorrect === true ? "#10B981" : qr.isCorrect === false ? "#EF4444" : "rgba(0,0,0,0.10)";
                          const bgColor = qr.isCorrect === true ? "#F0FDF4" : qr.isCorrect === false ? "#FEF2F2" : "#FAFAF8";
                          return (
                            <Paper key={qr.questionId} elevation={0} sx={{ p: { xs: 1.5, md: 2 }, borderRadius: "10px", border: `1.5px solid ${borderColor}`, bgcolor: bgColor }}>
                              <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", mb: 1 }}>
                                {qr.isCorrect === true && <CheckCircle sx={{ fontSize: 18, color: "#10B981", flexShrink: 0, mt: 0.25 }} />}
                                {qr.isCorrect === false && <Cancel sx={{ fontSize: 18, color: "#EF4444", flexShrink: 0, mt: 0.25 }} />}
                                {qr.isCorrect === null && <RadioButtonUnchecked sx={{ fontSize: 18, color: "#9CA3AF", flexShrink: 0, mt: 0.25 }} />}
                                <Typography variant="body2" fontWeight={700} sx={{ color: "#1F1714", flex: 1, textAlign: "start" }}>
                                  {question?.text || tcd.survey.questionFallback(idx + 1)}
                                </Typography>
                              </Box>
                              <Box sx={{ pl: 3.2, textAlign: "start" }}>
                                <Typography variant="caption" sx={{ color: qr.isCorrect === false ? "#B91C1C" : "#4B5563", display: "block" }}>
                                  {tcd.survey.yourAnswer}{" "}
                                  <Box component="span" sx={{ fontWeight: 700 }}>{userAnswerDisplay}</Box>
                                </Typography>
                                {qr.isCorrect === false && correctAnswerDisplay && (
                                  <Typography variant="caption" sx={{ color: "#059669", display: "block", mt: 0.4 }}>
                                    {tcd.survey.correctAnswer}{" "}
                                    <Box component="span" sx={{ fontWeight: 700 }}>{correctAnswerDisplay}</Box>
                                  </Typography>
                                )}
                                {qr.isCorrect === null && (
                                  <Typography variant="caption" sx={{ color: "#9CA3AF", display: "block", mt: 0.3, fontStyle: "italic" }}>
                                    {tcd.survey.notAutoGraded}
                                  </Typography>
                                )}
                              </Box>
                            </Paper>
                          );
                        })}
                      </Stack>
                    </Box>
                  )}

                  {/* Action buttons */}
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} justifyContent="center" sx={{ pb: 1 }}>
                    <Button
                      variant="outlined"
                      onClick={() => { setSurveyAnswers({}); setSurveyResult(null); setSurveyError(null); }}
                      sx={{ textTransform: "none", borderRadius: "10px", px: 3, minHeight: 44 }}
                    >
                      {tcd.survey.retakeQuiz}
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => setSurveyOpen(false)}
                      sx={{ textTransform: "none", borderRadius: "10px", px: 3, minHeight: 44, fontWeight: 700, bgcolor: "#7C3AED", "&:hover": { bgcolor: "#6D28D9" } }}
                    >
                      {tcd.survey.close}
                    </Button>
                  </Stack>
                </Box>
              ) : (
                /* Survey result: simple thank-you */
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", py: { xs: 4, md: 6 }, gap: 2 }}>
                  <Box sx={{ width: 80, height: 80, borderRadius: "50%", bgcolor: "#F5F3FF", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <CheckCircle sx={{ fontSize: 44, color: "#7C3AED" }} />
                  </Box>
                  <Typography variant="h5" fontWeight={800} sx={{ color: "#1F1714" }}>{tcd.survey.thankYou}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 320 }}>
                    {tcd.survey.thankYouBody}
                  </Typography>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 1, width: "100%", justifyContent: "center" }}>
                    <Button
                      variant="outlined"
                      onClick={() => { setSurveyAnswers({}); setSurveyResult(null); setSurveyError(null); }}
                      sx={{ textTransform: "none", borderRadius: "10px", px: 3 }}
                    >
                      {tcd.survey.submitAgain}
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => setSurveyOpen(false)}
                      sx={{ textTransform: "none", borderRadius: "10px", px: 3, bgcolor: "#7C3AED", "&:hover": { bgcolor: "#6D28D9" } }}
                    >
                      {tcd.survey.close}
                    </Button>
                  </Stack>
                </Box>
              )}
            </>
          ) : (
            /* ─── Questions Screen ─── */
            <Box>
              {/* Description */}
              {surveyItem?.surveyConfig?.description && (
                <Alert
                  severity="info"
                  sx={{ mb: 2.5, borderRadius: "10px", fontSize: "0.9rem" }}
                >
                  {localized(surveyItem.surveyConfig.description, surveyItem.surveyConfig.descriptionAr)}
                </Alert>
              )}

              {surveyItem?.surveyConfig?.mode === "quiz" && (
                <Alert
                  severity="warning"
                  sx={{ mb: 2.5, borderRadius: "10px", fontSize: "0.88rem" }}
                >
                  {tcd.survey.passingScore(surveyItem.surveyConfig.passingScore ?? 60)}
                </Alert>
              )}

              {surveyError && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }}>
                  {surveyError}
                </Alert>
              )}

              <Stack spacing={{ xs: 2.5, md: 3 }}>
                {(surveyItem?.surveyConfig?.questions ?? []).map((q: SurveyQuestion, qIdx: number) => (
                  <Paper
                    key={q.id}
                    elevation={0}
                    sx={{
                      p: { xs: 1.8, md: 2.2 },
                      borderRadius: "12px",
                      border: "1px solid rgba(0,0,0,0.08)",
                      bgcolor: "#FAFAF8",
                    }}
                  >
                    {/* Question header */}
                    <Box sx={{ display: "flex", gap: 1.2, mb: 1.8, alignItems: "flex-start" }}>
                      <Box
                        sx={{
                          minWidth: 28, height: 28, borderRadius: "8px",
                          bgcolor: "#F5F3FF",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          flexShrink: 0, mt: 0.1,
                        }}
                      >
                        <Typography
                          variant="caption"
                          fontWeight={800}
                          sx={{ color: "#7C3AED", fontSize: "0.75rem" }}
                        >
                          {qIdx + 1}
                        </Typography>
                      </Box>
                      <Typography
                        variant="body1"
                        fontWeight={700}
                        sx={{ lineHeight: 1.5, color: "#1F1714", flex: 1 }}
                      >
                        {q.text || tcd.survey.questionFallback(qIdx + 1)}
                        {q.required && (
                          <Typography
                            component="span"
                            sx={{ color: "#EF4444", ml: 0.4, fontWeight: 700 }}
                          >
                            *
                          </Typography>
                        )}
                      </Typography>
                    </Box>

                    {/* Answer input */}
                    {q.type === "radio" && (
                      <RadioGroup
                        value={surveyAnswers[q.id] ?? ""}
                        onChange={(_, v) =>
                          setSurveyAnswers((prev) => ({ ...prev, [q.id]: v }))
                        }
                        sx={{ pl: { xs: 0.5, md: 1 } }}
                      >
                        {q.options.map((opt) => (
                          <FormControlLabel
                            key={opt}
                            value={opt}
                            control={
                              <Radio
                                sx={{
                                  color: "#9CA3AF",
                                  "&.Mui-checked": { color: "#7C3AED" },
                                  p: { xs: 0.8, md: 1 },
                                }}
                              />
                            }
                            label={
                              <Typography
                                sx={{
                                  fontSize: { xs: "0.95rem", md: "1rem" },
                                  fontWeight: surveyAnswers[q.id] === opt ? 600 : 400,
                                  color: "#3D3630",
                                }}
                              >
                                {opt}
                              </Typography>
                            }
                            sx={{
                              mx: 0, mb: 0.5,
                              p: { xs: "4px 8px", md: "4px 12px" },
                              borderRadius: "8px",
                              border: "1px solid",
                              borderColor: surveyAnswers[q.id] === opt
                                ? "#7C3AED"
                                : "rgba(0,0,0,0.07)",
                              bgcolor: surveyAnswers[q.id] === opt
                                ? "#F5F3FF"
                                : "#FFFFFF",
                              transition: "all 0.12s",
                              "&:hover": { borderColor: "#7C3AED", bgcolor: "#F5F3FF" },
                            }}
                          />
                        ))}
                      </RadioGroup>
                    )}

                    {q.type === "checkbox" && (
                      <Box sx={{ pl: { xs: 0.5, md: 1 } }}>
                        {q.options.map((opt) => {
                          const checked = Array.isArray(surveyAnswers[q.id])
                            ? (surveyAnswers[q.id] as string[]).includes(opt)
                            : false;
                          return (
                            <FormControlLabel
                              key={opt}
                              control={
                                <Checkbox
                                  checked={checked}
                                  onChange={(_, c) => {
                                    const prev = Array.isArray(surveyAnswers[q.id])
                                      ? [...(surveyAnswers[q.id] as string[])]
                                      : [];
                                    setSurveyAnswers((s) => ({
                                      ...s,
                                      [q.id]: c
                                        ? [...prev, opt]
                                        : prev.filter((x) => x !== opt),
                                    }));
                                  }}
                                  sx={{
                                    color: "#9CA3AF",
                                    "&.Mui-checked": { color: "#7C3AED" },
                                    p: { xs: 0.8, md: 1 },
                                  }}
                                />
                              }
                              label={
                                <Typography
                                  sx={{
                                    fontSize: { xs: "0.95rem", md: "1rem" },
                                    fontWeight: checked ? 600 : 400,
                                    color: "#3D3630",
                                  }}
                                >
                                  {opt}
                                </Typography>
                              }
                              sx={{
                                display: "flex", mx: 0, mb: 0.5,
                                p: { xs: "4px 8px", md: "4px 12px" },
                                borderRadius: "8px",
                                border: "1px solid",
                                borderColor: checked ? "#7C3AED" : "rgba(0,0,0,0.07)",
                                bgcolor: checked ? "#F5F3FF" : "#FFFFFF",
                                transition: "all 0.12s",
                                "&:hover": { borderColor: "#7C3AED", bgcolor: "#F5F3FF" },
                              }}
                            />
                          );
                        })}
                      </Box>
                    )}

                    {q.type === "text" && (
                      <TextField
                        fullWidth
                        multiline
                        minRows={isMobile ? 3 : 4}
                        maxRows={8}
                        placeholder={tcd.survey.typeAnswer}
                        value={surveyAnswers[q.id] ?? ""}
                        onChange={(e) =>
                          setSurveyAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))
                        }
                        inputProps={{ maxLength: 2000 }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                            fontSize: { xs: "0.95rem", md: "1rem" },
                            bgcolor: "#FFFFFF",
                          },
                        }}
                        helperText={`${String(surveyAnswers[q.id] ?? "").length}/2000`}
                      />
                    )}

                    {q.type === "dropdown" && (
                      <FormControl fullWidth>
                        <InputLabel>{tcd.survey.chooseOption}</InputLabel>
                        <Select
                          value={surveyAnswers[q.id] ?? ""}
                          label={tcd.survey.chooseOption}
                          onChange={(e) =>
                            setSurveyAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))
                          }
                          sx={{
                            borderRadius: "10px",
                            bgcolor: "#FFFFFF",
                            fontSize: { xs: "0.95rem", md: "1rem" },
                          }}
                        >
                          {q.options.map((opt) => (
                            <MenuItem key={opt} value={opt}>
                              {opt}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Paper>
                ))}
              </Stack>
            </Box>
          )}
        </DialogContent>

        {/* Footer — submit button */}
        {!surveyResult && (
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: { xs: 1.8, md: 2 },
              borderTop: "1px solid rgba(0,0,0,0.08)",
              flexShrink: 0,
              bgcolor: "#FFFFFF",
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.2}
              justifyContent="flex-end"
            >
              <Button
                variant="outlined"
                onClick={() => setSurveyOpen(false)}
                disabled={surveySubmitting}
                sx={{
                  textTransform: "none",
                  borderRadius: "10px",
                  px: 3,
                  minHeight: 44,
                  order: { xs: 2, sm: 1 },
                }}
              >
                {tcd.survey.cancel}
              </Button>
              <Button
                variant="contained"
                onClick={submitSurvey}
                disabled={surveySubmitting}
                startIcon={
                  surveySubmitting
                    ? <CircularProgress size={18} sx={{ color: "#FFFFFF" }} />
                    : <Send />
                }
                sx={{
                  textTransform: "none",
                  borderRadius: "10px",
                  px: 4,
                  minHeight: 44,
                  fontWeight: 700,
                  order: { xs: 1, sm: 2 },
                  bgcolor: "#7C3AED",
                  "&:hover": { bgcolor: "#6D28D9" },
                  "&.Mui-disabled": { bgcolor: "#C4B5FD", color: "#FFFFFF" },
                }}
              >
                {surveySubmitting ? tcd.survey.submitting : tcd.survey.submit}
              </Button>
            </Stack>
          </Box>
        )}
      </Dialog>
    </Box>
  );
};

export default CourseDetail;
